import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";

import { getMyOrg, type Org } from "@/libs/onboardingApi";
import { useSelectedOrganization } from "@/app/OrganizationContext";

function orgDisplayName(org: Org): string {
  return org.business_name ?? org.name ?? "Your organization";
}

export function PendingConfirmationPage() {
  const { organizationId } = useSelectedOrganization();
  const { data: org, isLoading } = useQuery({
    queryKey: ["org", "me", organizationId],
    queryFn: () => getMyOrg(organizationId),
  });

  if (isLoading) {
    return (
      <Box
        aria-label="Loading registration status"
        role="status"
        sx={{ display: "grid", minHeight: "60vh", placeItems: "center" }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (org && (org.status === "approved" || org.status === "active")) {
    return <Navigate to="/getting-started" replace />;
  }

  return (
    <Stack
      spacing={3}
      sx={{
        width: "100%",
        maxWidth: 560,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: 6,
      }}
    >
      <Stack spacing={2} sx={{ alignItems: "center", textAlign: "center" }}>
        <Avatar
          sx={{
            width: 72,
            height: 72,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <ScheduleRoundedIcon sx={{ fontSize: 36 }} />
        </Avatar>
        <Stack spacing={1}>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 750 }}>
            Registration Under Review
          </Typography>
          <Typography color="text.secondary">
            Thank you for registering. Our team is reviewing your application.
            We will email you when the review is complete.
          </Typography>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2} divider={<Divider flexItem />}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-between" }}
          >
            <Typography color="text.secondary" variant="body2">
              Status
            </Typography>
            <Typography
              color="primary.main"
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              In review
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-between" }}
          >
            <Typography color="text.secondary" variant="body2">
              Business name
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, textAlign: "right" }}
            >
              {org ? orgDisplayName(org) : "—"}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      <Typography
        color="text.secondary"
        variant="caption"
        sx={{ textAlign: "center" }}
      >
        Reviews typically take 1-2 business days. We will notify you by email.
      </Typography>
    </Stack>
  );
}
